import React from 'react';
import { useSurvey } from '../../../context/SurveyContext';
import { 
  GripVertical, 
  Copy, 
  Trash2,
  CircleDot, 
  CheckSquare, 
  ChevronDown, 
  Gauge, 
  Star, 
  Type, 
  AlignLeft, 
  ToggleLeft, 
  Mail, 
  Hash 
} from 'lucide-react';

const typeIcons = {
  multipleChoice: CircleDot,
  checkboxes: CheckSquare,
  dropdown: ChevronDown,
  linearScale: Gauge,
  likertScale: Star,
  shortAnswer: Type,
  longAnswer: AlignLeft,
  yesNo: ToggleLeft,
  email: Mail,
  number: Hash
};

const typeLabels = {
  multipleChoice: 'Multiple Choice',
  checkboxes: 'Checkboxes',
  dropdown: 'Dropdown',
  linearScale: 'Linear Scale',
  likertScale: 'Likert Scale',
  shortAnswer: 'Short Answer',
  longAnswer: 'Long Answer',
  yesNo: 'Yes / No',
  email: 'Email',
  number: 'Number'
};

const QuestionCard = ({ question, index, onDragStart, onDragEnd, onDragOver, onDrop }) => {
  const { selectedQuestionId, setSelectedQuestionId, duplicateQuestion, deleteQuestion } = useSurvey();
  
  const Icon = typeIcons[question.type] || CircleDot;
  const isSelected = selectedQuestionId === question.id;

  const handleClick = () => {
    setSelectedQuestionId(question.id);
  };

  const handleDuplicate = (e) => {
    e.stopPropagation();
    duplicateQuestion(question.id);
  };

  const handleDelete = (e) => {
    e.stopPropagation();
    if (window.confirm('Delete this question?')) {
      deleteQuestion(question.id);
    }
  };

  const renderPreview = () => {
    switch (question.type) {
      case 'multipleChoice':
      case 'checkboxes':
        return (
          <div className="preview-options">
            {question.options?.slice(0, 3).map((opt, i) => (
              <div key={i} className="preview-option">
                {question.type === 'multipleChoice' ? '○' : '☐'} {opt.text}
              </div>
            ))}
            {question.options?.length > 3 && (
              <div className="preview-more">+{question.options.length - 3} more</div>
            )}
          </div>
        );
      case 'linearScale':
        return (
          <div className="preview-scale">
            <span>{question.minLabel || question.scaleMin}</span>
            <div className="scale-dots">
              {Array.from({ length: (question.scaleMax || 5) - (question.scaleMin || 1) + 1 }).map((_, i) => (
                <span key={i} className="scale-dot">○</span>
              ))}
            </div>
            <span>{question.maxLabel || question.scaleMax}</span>
          </div>
        );
      case 'likertScale':
        return (
          <div className="preview-likert">
            {question.labels?.slice(0, 3).join(' • ')}...
          </div>
        );
      case 'yesNo':
        return <div className="preview-yesno">○ Yes  ○ No</div>;
      case 'shortAnswer':
        return <div className="preview-input">Short text response...</div>;
      case 'longAnswer':
        return <div className="preview-textarea">Long text response...</div>;
      case 'email':
        return <div className="preview-input">email@example.com</div>;
      case 'number':
        return <div className="preview-input">123</div>;
      default:
        return null;
    }
  };

  return (
    <div
      className={`question-card ${isSelected ? 'selected' : ''}`}
      onClick={handleClick}
      draggable
      onDragStart={onDragStart}
      onDragEnd={onDragEnd}
      onDragOver={onDragOver}
      onDrop={onDrop}
    >
      <div className="question-card-header">
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          <div className="drag-handle">
            <GripVertical size={18} />
          </div>
          <div className="question-number">{index + 1}</div>
        </div>
        <div className="question-actions">
          <button className="question-action-btn" onClick={handleDuplicate} title="Duplicate">
            <Copy size={16} />
          </button>
          <button className="question-action-btn delete" onClick={handleDelete} title="Delete">
            <Trash2 size={16} />
          </button>
        </div>
      </div>
      
      <div className="question-text">
        {question.text}
        {question.required && <span className="required-badge">*</span>}
      </div>
      
      <div className="question-type-label">
        <Icon size={14} />
        {typeLabels[question.type]}
      </div>

      <div className="question-preview">
        {renderPreview()}
      </div>

      <style>{`
        .preview-options {
          font-size: 13px;
          color: #64748b;
        }
        .preview-option {
          padding: 4px 0;
        }
        .preview-more {
          font-style: italic;
          color: #94a3b8;
          padding-top: 4px;
        }
        .preview-scale {
          display: flex;
          align-items: center;
          gap: 12px;
          font-size: 12px;
          color: #64748b;
        }
        .scale-dots {
          display: flex;
          gap: 8px;
        }
        .scale-dot {
          font-size: 16px;
        }
        .preview-likert {
          font-size: 12px;
          color: #64748b;
        }
        .preview-yesno {
          font-size: 13px;
          color: #64748b;
        }
        .preview-input {
          padding: 8px 12px;
          background: #f8fafc;
          border: 1px dashed #e2e8f0;
          border-radius: 6px;
          font-size: 13px;
          color: #94a3b8;
        }
        .preview-textarea {
          padding: 8px 12px;
          background: #f8fafc;
          border: 1px dashed #e2e8f0;
          border-radius: 6px;
          font-size: 13px;
          color: #94a3b8;
          min-height: 60px;
        }
      `}</style>
    </div>
  );
};

export default QuestionCard;
